import { useQuery } from '@apollo/client';
import { useTranslation } from 'react-i18next';
import { useContext, useEffect, useState } from 'react';
import dayjs from 'dayjs';
import { usePermissions, UserContext } from '../../../../providers/userContextProvider';
import { FETCH_CUSTODIAN_ACTIVITIES } from './custodianActivities.queries';
import { Box, Skeleton, Typography } from '../../../1-primative';
import {
  Table, TableRow, TableCell, TableBody, MenuItem, TableHeadCell, Card, CardContent, SelectField, Pagination,
  DateField,
} from '../../../2-component';
import { FilterModal, SecuritySelect } from '../../../3-pattern';
import { translateBackend } from '../../../../assets/i18n/config';
import DownloadCustodianActivities from './components/downloadCustodianActivities';
import { CustodianActivityModal } from './components/custodianActivityModal';
import { CustodianActivityCell } from './components/custodianActivityCell';
import { CustodianActivity, CustodianActivityStates, CustodianActivityTransactionTypes } from '../../../../interfaces';

const PAGE_SIZE = 15;

export const CustodianActivities = ({ accountId, organizationId, options }: { accountId: string, organizationId: string, options: any }) => {
  const { t } = useTranslation(['components', 'transfer']);
  const { permissions } = usePermissions();
  const { activeOrganization } = useContext(UserContext);
  const [page, setPage] = useState(1);
  const [state, setState] = useState('any');
  const [transactionType, setTransactionType] = useState<string>('any');
  const [startDate, setStartDate] = useState<string>('');
  const [endDate, setEndDate] = useState<string>('');

  const [activeActivity, setActiveActivity] = useState<CustodianActivity | null>(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [perPage, setPerPage] = useState<number>(PAGE_SIZE);
  const [financialProductId, setFinancialProductId] = useState<string>('ANY');

  const showDownloadbutton = permissions.includes('read:api_exports') && options.enableDownload;

  const queryFilter = {
    organizationId: organizationId || activeOrganization.id,
    ...(accountId ? { accountId } : {}),
    financialProductId: financialProductId === 'ANY' ? undefined : financialProductId,
    ...((state && state !== 'any') ? { state } : {}),
    ...((transactionType && transactionType !== 'any') ? { transactionType } : {}),
    ...((startDate && startDate !== '' && dayjs(startDate).isValid()) ? { startDate: dayjs(startDate).format('YYYY-MM-DD') } : {}),
    ...((endDate && endDate !== '' && dayjs(endDate).isValid()) ? { endDate: dayjs(endDate).format('YYYY-MM-DD') } : {}),
  };

  const {
    loading, previousData, data, refetch,
  } = useQuery(FETCH_CUSTODIAN_ACTIVITIES(permissions), {
    variables: {
      input: {
        filter: { ...queryFilter },
        pagination: {
          sortField: 'effectiveDate', sortDesc: true, perPage, offSet: (page - 1) * perPage,
        },
      },
    },
    notifyOnNetworkStatusChange: true,
    skip: !permissions.includes('read:custodian_activity_basic'),
  });

  useEffect(() => function cleanupOnUnmount() {
    setState('any');
    setTransactionType('any');
    setPage(1);
  }, []);

  if (!permissions.includes('read:custodian_activity_basic')) return <></>;

  const totalCount = (data || previousData)?.fetchCustodianActivities?.totalCount || 0;
  const showPagination = totalCount > PAGE_SIZE;

  const DEFAULT_TABLE = [
    { label: { en: t('components:custodianActivities.accountType') }, type: 'accountType' as const },
    { label: { en: t('components:custodianActivities.accountNumber') }, type: 'accountNumber' as const },
    { label: { en: t('components:custodianActivities.transactionType') }, type: 'transactionType' as const },
    { label: { en: t('components:custodianActivities.transactionDesc') }, type: 'transactionDesc' as const },
    { label: { en: t('components:custodianActivities.currency') }, type: 'currency' as const },
    { label: { en: t('components:custodianActivities.security') }, type: 'security' as const },
    { label: { en: t('components:custodianActivities.quantity') }, type: 'quantity' as const },
    { label: { en: t('components:custodianActivities.price') }, type: 'price' as const },
    { label: { en: t('components:custodianActivities.amount') }, type: 'amount' as const },
    { label: { en: t('components:custodianActivities.state') }, type: 'state' as const },
    { label: { en: t('components:custodianActivities.effectiveDate') }, type: 'effectiveDate' as const },
    { label: { en: t('components:custodianActivities.processDate') }, type: 'processDate' as const },
  ];

  const filterExists = (!!state && state !== 'any') || (!!financialProductId && financialProductId !== 'ANY') || (!!transactionType && transactionType !== 'any') || !!startDate || !!endDate;

  return (
    <Box>
      {options.customTitle && <Typography variant='headingSmall' sx={{ mb: 2 }}>{translateBackend(options.customTitle)}</Typography>}
      <Card>
        {(showDownloadbutton || options.enableFilter) && (
          <CardContent>
            <Box display='flex' justifyContent='end' alignItems='end'>
              <Box display='flex'>
                {options.enableFilter && (
                  <FilterModal sx={{ mr: 1, ml: 1 }} filterExists={filterExists}>
                    <SelectField
                      value={state}
                      label={t('components:custodianActivities.state')}
                      onChange={(e: any) => {
                        setState(e.target.value);
                        setPage(1);
                      }}
                      size='medium'
                      fullWidth
                      placeholder={t('any')}
                      sx={{ mb: 2 }}
                    >
                      <MenuItem value={'any'}>{t('any')}</MenuItem>
                      {Object.values(CustodianActivityStates).map((x: any) => <MenuItem value={x} key={x}>{t(`reconciliation:activityState.${x}`)}</MenuItem>)}
                    </SelectField>
                    <SecuritySelect withAny
                      value={financialProductId ?? 'ANY'}
                      setSecurity={(e: any) => {
                        setFinancialProductId(e?.id ?? e);
                        setPage(1);
                      }}
                      label={t('components:security')}
                      organizationId={organizationId}
                      sx={{ mb: 2 }}
                    />
                    <SelectField
                      value={transactionType ?? 'any'}
                      label={t('components:custodianActivities.transactionType')}
                      onChange={(e: any) => {
                        setTransactionType(e.target.value);
                        setPage(1);
                      }}
                      size='medium'
                      fullWidth
                      placeholder={t('any')}
                      sx={{ mb: 2 }}
                    >
                      <MenuItem value={'any'}>{t('any')}</MenuItem>
                      {Object.values(CustodianActivityTransactionTypes).map((x: any) => <MenuItem value={x} key={x}>{x}</MenuItem>)}
                    </SelectField>
                    {/* Date filter */}
                    <DateField
                      label={t('components:fromDate')}
                      value={startDate}
                      onChange={(e: any) => {
                        setStartDate(e);
                        setPage(1);
                      }}
                      fullWidth
                      sx={{ mb: 2 }}
                    />
                    <DateField
                      label={t('components:toDate')}
                      value={endDate}
                      onChange={(e: any) => {
                        setEndDate(e);
                        setPage(1);
                      }}
                      fullWidth
                      sx={{ mb: 2 }}
                    />
                  </FilterModal>
                )}
                {showDownloadbutton && <DownloadCustodianActivities filter={queryFilter} />}
              </Box>
            </Box>
          </CardContent>
        )}
        <Box sx={{ overflowX: 'auto' }}>
          <Table aria-label="table">
            <TableBody>
              <TableRow>
                {(options.table || DEFAULT_TABLE).map((x: any, idx: number) => <TableHeadCell key={x} isFirst={idx === 0}>{translateBackend(x.label)}</TableHeadCell>)}
              </TableRow>
              {loading && !previousData && [...Array(15)].map((x: any, i: number) => (
                <TableRow key={i}>
                  {(options.table || DEFAULT_TABLE).map((_: any, idx: number) => <TableCell dense isFirst={idx === 0}><Skeleton width='100%' height='16px'/></TableCell>)}
                </TableRow>
              ))}
              {((data || previousData)?.fetchCustodianActivities?.custodianActivities || []).map((custodianActivity: CustodianActivity) => (
                <TableRow hover pointer key={custodianActivity.id} onClick={() => {
                  setActiveActivity(custodianActivity);
                  setModalOpen(true);
                }}>
                  {(options.table || DEFAULT_TABLE).map((x: any, i: number) => <CustodianActivityCell key={i} isFirst={i === 0} type={x.type} custodianActivity={custodianActivity} />) }
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Box>

        {showPagination && (
          <Box sx={{ px: 2, py: 1 }}>
            <Pagination
              size='small'
              page={page}
              total={totalCount}
              perPage={perPage}
              count={Math.ceil(totalCount / perPage)}
              onChange={(e, newPage) => setPage(newPage)}
              onChangePerPage={(newPerPage) => {
                setPerPage(newPerPage);
                setPage(1);
              }}
            />
          </Box>
        )}
        {activeActivity && (
          <CustodianActivityModal options={options} activity={activeActivity} open={modalOpen} setOpen={setModalOpen} onTransition={refetch}/>
        )}
      </Card>
    </Box>
  );
};

export default CustodianActivities;
